import ipaddr from "ipaddr.js";

export type AddressFamily = "ipv4" | "ipv6";

export interface CidrPlan {
  family: AddressFamily;
  bits: 32 | 128;
  inputAddress: string;
  prefix: number;
  normalizedCidr: string;
  networkAddress: string;
  lastAddress: string;
  totalAddresses: bigint;
  netmask: string | null;
  wildcardMask: string | null;
  broadcastAddress: string | null;
  firstUsable: string | null;
  lastUsable: string | null;
  usableAddresses: bigint | null;
  isAligned: boolean;
  note: string;
}

export interface SubnetRow {
  index: number;
  cidr: string;
  firstAddress: string;
  lastAddress: string;
  addresses: bigint;
}

export interface SubnetPreview {
  newPrefix: number;
  totalSubnets: bigint;
  shownSubnets: number;
  truncated: boolean;
  rows: SubnetRow[];
}

function bytesToBigInt(bytes: number[]): bigint {
  return bytes.reduce((value, byte) => (value << 8n) | BigInt(byte), 0n);
}

function bigIntToBytes(value: bigint, byteLength: number): number[] {
  const bytes = new Array<number>(byteLength).fill(0);
  let remaining = value;
  for (let index = byteLength - 1; index >= 0; index -= 1) {
    bytes[index] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return bytes;
}

function formatAddress(value: bigint, bits: 32 | 128): string {
  return ipaddr.fromByteArray(bigIntToBytes(value, bits / 8)).toString();
}

function parseStrictCidr(input: string) {
  const value = input.trim();
  if (!value || value.includes("%") || !value.includes("/")) {
    throw new Error("请输入带前缀长度的 IPv4 或 IPv6 CIDR，例如 192.168.10.42/24。");
  }

  const addressText = value.slice(0, value.lastIndexOf("/"));
  const isIpv6 = addressText.includes(":");
  const valid = isIpv6
    ? ipaddr.IPv6.isValidCIDR(value)
    : ipaddr.IPv4.isValidCIDRFourPartDecimal(value);
  if (!valid) {
    throw new Error("CIDR 格式无效。IPv4 请使用四段十进制，IPv6 不支持接口区域标识符。");
  }

  const [address, prefix] = ipaddr.parseCIDR(value);
  const family: AddressFamily = address.kind();
  const bits = family === "ipv4" ? 32 : 128;
  return { address, prefix, family, bits } as const;
}

export function planCidr(input: string): CidrPlan {
  const { address, prefix, family, bits } = parseStrictCidr(input);
  const inputValue = bytesToBigInt(address.toByteArray());
  const hostBits = bits - prefix;
  const totalAddresses = 1n << BigInt(hostBits);
  const allBits = (1n << BigInt(bits)) - 1n;
  const hostMask = totalAddresses - 1n;
  const networkValue = inputValue & (allBits ^ hostMask);
  const lastValue = networkValue + hostMask;
  const networkAddress = formatAddress(networkValue, bits);
  const lastAddress = formatAddress(lastValue, bits);

  if (family === "ipv6") {
    return {
      family,
      bits,
      inputAddress: address.toString(),
      prefix,
      normalizedCidr: `${networkAddress}/${prefix}`,
      networkAddress,
      lastAddress,
      totalAddresses,
      netmask: null,
      wildcardMask: null,
      broadcastAddress: null,
      firstUsable: null,
      lastUsable: null,
      usableAddresses: null,
      isAligned: inputValue === networkValue,
      note: "IPv6 使用组播而不是广播；可分配地址还取决于链路类型和网络策略。",
    };
  }

  const netmaskValue = allBits ^ hostMask;
  let firstUsable = networkAddress;
  let lastUsable = lastAddress;
  let usableAddresses = totalAddresses;
  let broadcastAddress: string | null = null;
  let note = "网络地址和定向广播地址不计入传统主机可用范围。";

  if (prefix <= 30) {
    firstUsable = formatAddress(networkValue + 1n, bits);
    lastUsable = formatAddress(lastValue - 1n, bits);
    usableAddresses = totalAddresses - 2n;
    broadcastAddress = lastAddress;
  } else if (prefix === 31) {
    note = "RFC 3021 点对点链路：两个端点地址均可使用，不设定向广播地址。";
  } else {
    note = "/32 表示单个 IPv4 主机路由。";
  }

  return {
    family,
    bits,
    inputAddress: address.toString(),
    prefix,
    normalizedCidr: `${networkAddress}/${prefix}`,
    networkAddress,
    lastAddress,
    totalAddresses,
    netmask: formatAddress(netmaskValue, bits),
    wildcardMask: formatAddress(hostMask, bits),
    broadcastAddress,
    firstUsable,
    lastUsable,
    usableAddresses,
    isAligned: inputValue === networkValue,
    note,
  };
}

export function previewSubnets(plan: CidrPlan, newPrefix: number, limit = 256): SubnetPreview {
  if (!Number.isInteger(newPrefix) || newPrefix < plan.prefix || newPrefix > plan.bits) {
    throw new Error(`新前缀必须是 /${plan.prefix} 到 /${plan.bits} 之间的整数。`);
  }
  if (!Number.isInteger(limit) || limit < 1 || limit > 256) {
    throw new Error("预览上限必须在 1 到 256 之间。");
  }

  const totalSubnets = 1n << BigInt(newPrefix - plan.prefix);
  const shownSubnets = Number(totalSubnets > BigInt(limit) ? BigInt(limit) : totalSubnets);
  const addresses = 1n << BigInt(plan.bits - newPrefix);
  const base = bytesToBigInt(ipaddr.parse(plan.networkAddress).toByteArray());
  const rows = Array.from({ length: shownSubnets }, (_, index) => {
    const first = base + BigInt(index) * addresses;
    const last = first + addresses - 1n;
    return {
      index: index + 1,
      cidr: `${formatAddress(first, plan.bits)}/${newPrefix}`,
      firstAddress: formatAddress(first, plan.bits),
      lastAddress: formatAddress(last, plan.bits),
      addresses,
    };
  });

  return {
    newPrefix,
    totalSubnets,
    shownSubnets,
    truncated: totalSubnets > BigInt(shownSubnets),
    rows,
  };
}

export function formatInteger(value: bigint): string {
  return value.toLocaleString("zh-CN");
}

export function planSummary(plan: CidrPlan): string {
  const lines = [
    `CIDR: ${plan.normalizedCidr}`,
    `地址族: ${plan.family === "ipv4" ? "IPv4" : "IPv6"}`,
    `网络地址: ${plan.networkAddress}`,
    `末地址: ${plan.lastAddress}`,
    `地址总数: ${formatInteger(plan.totalAddresses)}`,
  ];
  if (plan.netmask) lines.push(`子网掩码: ${plan.netmask}`);
  if (plan.wildcardMask) lines.push(`通配掩码: ${plan.wildcardMask}`);
  if (plan.broadcastAddress) lines.push(`定向广播: ${plan.broadcastAddress}`);
  if (plan.firstUsable && plan.lastUsable && plan.usableAddresses !== null) {
    lines.push(`可用范围: ${plan.firstUsable} - ${plan.lastUsable}`);
    lines.push(`可用地址: ${formatInteger(plan.usableAddresses)}`);
  }
  lines.push(`说明: ${plan.note}`);
  return lines.join("\n");
}

export function subnetPreviewCsv(preview: SubnetPreview): string {
  const rows = ["index,cidr,first_address,last_address,address_count"];
  for (const row of preview.rows) {
    rows.push([row.index, row.cidr, row.firstAddress, row.lastAddress, row.addresses.toString()].join(","));
  }
  return `${rows.join("\n")}\n`;
}
